import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Sidebar from './Sidebar';
import MetaData from '../Layouts/MetaData';
import Loader from '../Layouts/Loader';

const stripePromise = loadStripe('pk_live_xWzWSyu3bi0iBdbBeqftvUdn');

const elementStyle = {
    style: {
        base: {
            fontSize: '15px',
            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
            color: '#1a1a1a',
            '::placeholder': { color: '#9ca3af' },
        },
        invalid: { color: '#d33' },
    },
};

const REASON_LABELS = {
    order_reward: 'Points earned',
    order_redemption: 'Points redeemed',
    order_cash_payment: 'Paid with 1313 Cash',
    cash_topup: '1313 Cash top-up',
    reward_clawback: 'Points clawed back',
    admin_adjustment: 'Admin adjustment',
};

const MyWalletInner = () => {
    const { enqueueSnackbar } = useSnackbar();
    const stripe = useStripe();
    const elements = useElements();

    const [loading, setLoading] = useState(true);
    const [wallet, setWallet] = useState(null);
    const [settings, setSettings] = useState(null);

    const [topupAmount, setTopupAmount] = useState('');
    const [topupIntent, setTopupIntent] = useState(null);
    const [processing, setProcessing] = useState(false);

    const fetchWallet = async () => {
        try {
            const { data } = await axios.get('/api/v1/my-wallet');
            setWallet(data);
        } catch (error) {
            enqueueSnackbar("Failed to load your wallet", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWallet();
        axios.get('/api/v1/wallet-settings').then(({ data }) => setSettings(data.settings)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startTopup = async (e) => {
        e.preventDefault();
        const amount = Number(topupAmount);
        if (!amount || amount <= 0 || (settings && amount % settings.cashTopupMultiple !== 0)) {
            enqueueSnackbar(`Amount must be a multiple of HK$${settings?.cashTopupMultiple ?? 100}`, { variant: "error" });
            return;
        }
        try {
            const { data } = await axios.post('/api/v1/my-wallet/topup-intent', { amount });
            setTopupIntent(data);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to start top-up", { variant: "error" });
        }
    };

    const confirmTopup = async () => {
        if (!stripe || !topupIntent) return;
        setProcessing(true);
        try {
            const { error, paymentIntent } = await stripe.confirmCardPayment(topupIntent.clientSecret, {
                payment_method: { card: elements.getElement(CardNumberElement) },
            });
            if (error) throw new Error(error.message);

            await axios.post('/api/v1/my-wallet/confirm-topup', { paymentIntentId: paymentIntent.id });
            enqueueSnackbar(`HK$${topupIntent.amount.toLocaleString()} added to your 1313 Cash`, { variant: "success" });
            setTopupIntent(null);
            setTopupAmount('');
            fetchWallet();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || error.message || "Top-up failed", { variant: "error" });
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="flex flex-col sm:flex-row gap-4 w-11/12 sm:w-10/12 mx-auto my-6">
            <Sidebar activeTab="wallet" />

            <div className="flex flex-col gap-4 w-full">
                <MetaData title="My Wallet | 1313 Mart" />

                <h1 className="text-lg font-medium">1313 Wallet</h1>

                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 bg-white shadow rounded-sm p-5">
                        <p className="text-xs text-gray-400 uppercase">1313 Points</p>
                        <p className="text-2xl font-semibold text-primary-orange">{wallet.points.toLocaleString()}</p>
                        {settings && <p className="text-xs text-gray-400 mt-1">{settings.pointsToCurrencyRedemption} points = HK$1 off your next order</p>}
                    </div>
                    <div className="flex-1 bg-white shadow rounded-sm p-5">
                        <p className="text-xs text-gray-400 uppercase">1313 Cash</p>
                        <p className="text-2xl font-semibold text-primary-green">HK${wallet.cash.toLocaleString()}</p>
                        <p className="text-xs text-gray-400 mt-1">Real money you've added — spend it like cash at checkout</p>
                    </div>
                </div>

                <div className="bg-white shadow rounded-sm p-5">
                    <h3 className="font-medium mb-3">Add 1313 Cash</h3>
                    {!topupIntent ? (
                        <form onSubmit={startTopup} className="flex flex-wrap items-end gap-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Amount (HK$)</label>
                                <input
                                    type="number"
                                    step={settings?.cashTopupMultiple ?? 100}
                                    value={topupAmount}
                                    onChange={(e) => setTopupAmount(e.target.value)}
                                    placeholder={`e.g. ${settings?.cashTopupMultiple ?? 100}`}
                                    className="border border-gray-300 rounded px-3 py-2 text-sm w-40"
                                />
                            </div>
                            <button type="submit" className="bg-primary-orange px-5 py-2 text-white text-sm font-medium rounded shadow hover:shadow-lg">
                                Continue
                            </button>
                            <p className="text-xs text-gray-400 w-full">Must be a multiple of HK${settings?.cashTopupMultiple ?? 100}.</p>
                        </form>
                    ) : (
                        <div className="flex flex-col gap-3 max-w-md">
                            <p className="text-sm text-gray-600">Adding <span className="font-medium">HK${topupIntent.amount.toLocaleString()}</span> to your 1313 Cash.</p>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Card Number</label>
                                <div className="border border-gray-300 rounded-md px-3 py-2.5"><CardNumberElement options={{ ...elementStyle, placeholder: '1234 1234 1234 1234' }} /></div>
                            </div>
                            <div className="flex gap-3">
                                <div className="flex-1">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Expiry Date</label>
                                    <div className="border border-gray-300 rounded-md px-3 py-2.5"><CardExpiryElement options={elementStyle} /></div>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">CVV</label>
                                    <div className="border border-gray-300 rounded-md px-3 py-2.5"><CardCvcElement options={elementStyle} /></div>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button onClick={() => setTopupIntent(null)} className="py-2.5 px-6 text-gray-600 text-sm font-medium">Cancel</button>
                                <button onClick={confirmTopup} disabled={processing} className="bg-primary-orange py-2.5 px-8 text-white text-sm font-medium shadow hover:shadow-lg rounded disabled:opacity-60">
                                    {processing ? "Processing..." : `Pay HK$${topupIntent.amount.toLocaleString()}`}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white shadow rounded-sm overflow-hidden">
                    <h3 className="font-medium p-5 pb-0">Transaction History</h3>
                    {wallet.transactions.length === 0 ? (
                        <p className="text-sm text-gray-400 p-5">No wallet activity yet.</p>
                    ) : (
                        <table className="w-full text-sm mt-3">
                            <thead>
                                <tr className="text-left text-gray-500 text-xs uppercase border-b">
                                    <th className="px-5 py-2">Date</th>
                                    <th className="px-5 py-2">Type</th>
                                    <th className="px-5 py-2">Reason</th>
                                    <th className="px-5 py-2 text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {wallet.transactions.map((txn) => (
                                    <tr key={txn._id} className="border-b last:border-0">
                                        <td className="px-5 py-2.5">{new Date(txn.createdAt).toLocaleDateString('en-HK')}</td>
                                        <td className="px-5 py-2.5">{txn.walletType === 'cash' ? '1313 Cash' : 'Points'}</td>
                                        <td className="px-5 py-2.5">
                                            {REASON_LABELS[txn.reason] || txn.reason}
                                            {txn.order && ` — Order #${txn.order.orderNumber ?? ''}`}
                                        </td>
                                        <td className={`px-5 py-2.5 text-right font-medium ${txn.direction === 'credit' ? 'text-primary-green' : 'text-red-500'}`}>
                                            {txn.direction === 'credit' ? '+' : '-'}{txn.walletType === 'cash' ? `HK$${txn.amount.toLocaleString()}` : txn.amount.toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

const MyWallet = () => (
    <Elements stripe={stripePromise}>
        <MyWalletInner />
    </Elements>
);

export default MyWallet;